/**
 * WorkflowQueueTable — schema-driven data table.
 *
 * Columns come entirely from WorkflowSurface.ui.queueColumns — no column is
 * hard-coded. Adding a workflow = adding its UiColumnSpec in the Registry.
 *
 * Owner requirements:
 *  #1 No if(workflow===...) — column rendering is type-based only.
 *  #8 Pagination rendered from queue page metadata (total/limit/offset).
 *
 * Preserves the existing table visual: same padding, font, border, hover bg,
 * chevron expand. The expand panel is WorkflowOrderPanel.
 */

import { useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@shopify/polaris-icons'
import { Button } from '@shopify/polaris'
import { WorkflowCellRenderer, colWidth } from './WorkflowCellRenderer'
import { WorkflowOrderPanel }             from './WorkflowOrderPanel'
import type { QueueEntry, WorkflowSurface, WorkflowPermissions } from '../../sdk/engine.api'

const TH: React.CSSProperties = {
  padding: '8px 16px', textAlign: 'left', fontSize: 12,
  fontWeight: 600, color: '#6d7175',
  borderBottom: '2px solid #e1e3e5',
  position: 'sticky', top: 0, background: '#f6f6f7', zIndex: 1,
}

const TD: React.CSSProperties = {
  padding: '12px 16px', borderTop: '1px solid #e1e3e5',
  verticalAlign: 'middle', fontSize: 14, color: '#202223',
}

type Props = {
  items:            QueueEntry[]
  total:            number
  limit:            number
  offset:           number
  surface:          WorkflowSurface
  permissions:      WorkflowPermissions
  shop:             string
  loading:          boolean
  onNextPage:       () => void
  onPrevPage:       () => void
  onMutated:        () => void
  /** Optional extra button shown in the expanded panel (e.g. Re-open on Completed tab) */
  extraPanelAction?: (entry: QueueEntry) => React.ReactNode
}

export function WorkflowQueueTable({
  items, total, limit, offset, surface, permissions,
  shop, loading, onNextPage, onPrevPage, onMutated, extraPanelAction,
}: Props) {
  const { ui, statusSet } = surface
  const cols = ui.queueColumns

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const colCount = cols.length + 1  // +1 for chevron

  const hasPrev = offset > 0
  const hasNext = offset + limit < total
  const fromNum = total === 0 ? 0 : offset + 1
  const toNum   = Math.min(offset + items.length, total)

  return (
    <div>
      {/* Table */}
      <div style={{ overflowX: 'auto', border: '1px solid #e1e3e5', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              {cols.map(col => (
                <th key={col.key} style={{ ...TH, minWidth: colWidth(col) }}>
                  {col.label}
                </th>
              ))}
              <th style={{ ...TH, width: 36, padding: '8px 12px' }} />
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr>
                <td colSpan={colCount} style={{ ...TD, textAlign: 'center', color: '#6d7175', padding: 32 }}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={colCount} style={{ ...TD, textAlign: 'center', color: '#6d7175', padding: 32 }}>
                  No entries
                </td>
              </tr>
            )}
            {items.map(entry => {
              const isExpanded = expandedId === entry.id
              const priorityBg = entry.priority === 'critical' ? '#fff4e5'
                               : entry.priority === 'high'     ? '#fffbe6'
                               : null
              const rowBg = isExpanded ? '#f6f6f7' : (priorityBg ?? '#fff')
              const borderLeft = entry.orderRef && (entry.priority === 'critical' || entry.priority === 'high')
                ? entry.priority === 'critical' ? '3px solid #d82c0d' : '3px solid #ffc453'
                : undefined

              return (
                <>
                  {/* Summary row */}
                  <tr
                    key={`row-${entry.id}`}
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    style={{ cursor: 'pointer', background: rowBg, borderLeft }}
                    onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = '#f6f6f7' }}
                    onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = rowBg }}
                  >
                    {cols.map(col => (
                      <td key={col.key} style={TD}>
                        <WorkflowCellRenderer col={col} entry={entry} statusSet={statusSet} />
                      </td>
                    ))}
                    <td style={{ ...TD, textAlign: 'right', padding: '12px 12px' }}>
                      <span style={{ color: '#8c9196', display: 'flex', justifyContent: 'flex-end' }}>
                        {isExpanded
                          ? <ChevronDownIcon  width={20} height={20} />
                          : <ChevronRightIcon width={20} height={20} />}
                      </span>
                    </td>
                  </tr>

                  {/* Expanded panel */}
                  {isExpanded && (
                    <tr key={`panel-${entry.id}`} style={{ background: '#f6f6f7' }}>
                      <td colSpan={colCount} style={{ padding: '16px 20px', borderTop: '1px solid #e1e3e5' }}>
                        <WorkflowOrderPanel
                          entry={entry}
                          surface={surface}
                          permissions={permissions}
                          shop={shop}
                          onClose={() => setExpandedId(null)}
                          onMutated={() => { setExpandedId(null); onMutated() }}
                          extraAction={extraPanelAction?.(entry)}
                        />
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination bar */}
      {total > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 0', marginTop: 8,
        }}>
          <span style={{ fontSize: 13, color: '#6d7175' }}>
            {fromNum}–{toNum} of {total}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button disabled={!hasPrev || loading} onClick={onPrevPage} size="slim">Previous</Button>
            <Button disabled={!hasNext || loading} onClick={onNextPage} size="slim">Next</Button>
          </div>
        </div>
      )}
    </div>
  )
}
